import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Landmark, Gavel, Loader2 } from "lucide-react";

const COURTS = ["Tribunale di Milano", "Tribunale di Napoli", "Corte d'Appello di Milano", "Corte d'Appello di Napoli", "Corte di Cassazione", "Giudice di Pace"];
const STATUSES = ["In attesa", "In corso", "Conclusa"];

const statusStyle = (s) => ({
  "In attesa": "bg-amber-500/15 text-amber-700 border-amber-500/30",
  "In corso": "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "Conclusa": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
}[s] || "bg-stone-200 text-stone-600");

export default function CourtTab() {
  const [hearings, setHearings] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCollab, setFilterCollab] = useState("all");
  const empty = { title: "", court: COURTS[0], date: new Date().toISOString().split("T")[0], time: "09:30", collaborator_name: "", judge: "", status: "In attesa", note: "" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, c] = await Promise.all([api.get("/hearings"), api.get("/collaborators")]);
      setHearings(h.data);
      setCollaborators(c.data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title || !form.collaborator_name) { toast.error("Compila titolo e collaboratore"); return; }
    setSaving(true);
    try {
      const res = await api.post("/hearings", form);
      setHearings((h) => [...h, res.data]);
      toast.success("Udienza aggiunta");
      setOpen(false);
      setForm(empty);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await api.patch(`/hearings/${id}`, { status });
      setHearings((h) => h.map((x) => (x.id === id ? res.data : x)));
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/hearings/${id}`); setHearings((h) => h.filter((x) => x.id !== id)); toast.success("Udienza rimossa"); }
    catch (e) { toast.error("Errore"); }
  };

  const filtered = hearings
    .filter((h) => filterCollab === "all" || h.collaborator_name === filterCollab)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div className="space-y-1.5">
          <Label className="text-stone-600 text-xs">Filtra per collaboratore</Label>
          <Select value={filterCollab} onValueChange={setFilterCollab}>
            <SelectTrigger data-testid="court-filter-collaborator" className="bg-[#FFFFFF] border-stone-300 text-stone-900 w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">
              <SelectItem value="all">Tutti i collaboratori</SelectItem>
              {collaborators.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-court-hearing-btn" className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Nuova Udienza
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-luxe rounded-xl p-12 text-center text-stone-500">Nessuna udienza in agenda.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((h) => (
            <div key={h.id} className="card-luxe rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="court-hearing-item">
              <div className="flex items-center justify-center w-16 h-16 rounded-lg bg-[#F4EFE4] border border-amber-500/20 shrink-0">
                <div className="text-center">
                  <div className="text-lg font-serif text-amber-600 leading-none">{h.time}</div>
                  <div className="text-[10px] text-stone-500 mt-1">{h.date.slice(5)}</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Gavel className="w-4 h-4 text-amber-600 shrink-0" />
                  <h3 className="font-serif text-lg text-stone-900 truncate">{h.title}</h3>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                  <span className="flex items-center gap-1"><Landmark className="w-3 h-3" /> {h.court}</span>
                  <span>Avv.: {h.collaborator_name}</span>
                  {h.judge && <span>Giudice: {h.judge}</span>}
                </div>
                {h.note && <div className="text-xs text-stone-500 mt-1">{h.note}</div>}
              </div>
              <div className="flex items-center gap-2">
                <Select value={h.status} onValueChange={(v) => setStatus(h.id, v)}>
                  <SelectTrigger data-testid={`hearing-status-${h.id}`} className={`w-32 h-9 text-xs border ${statusStyle(h.status)}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => remove(h.id)} data-testid={`delete-hearing-${h.id}`} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border border-amber-500/20 text-stone-900 max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nuova Udienza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Titolo causa *</Label>
              <Input value={form.title} data-testid="hearing-title-input" onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="Es. Rossi c/ Delta S.p.A." /></div>
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Tribunale / Corte</Label>
              <Select value={form.court} onValueChange={(v) => setForm((f) => ({ ...f, court: v }))}>
                <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{COURTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900 [color-scheme:light]" /></div>
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Ora</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900 [color-scheme:light]" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Avvocato incaricato *</Label>
              <Select value={form.collaborator_name} onValueChange={(v) => setForm((f) => ({ ...f, collaborator_name: v }))}>
                <SelectTrigger data-testid="hearing-collaborator-select" className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{collaborators.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Giudice</Label>
                <Input value={form.judge} onChange={(e) => setForm((f) => ({ ...f, judge: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900" /></div>
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Stato</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900 resize-none" rows={2} /></div>
            <Button onClick={save} disabled={saving} data-testid="save-hearing-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva Udienza"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
